import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import toast from '$$vueCmp/toast';
import app from '$$app';
import {
  buildAxiosErrorMeta,
  buildBrowserClientContext,
  isStoreApiGatewayFailure,
  isStoreApiNetworkFailure,
  resolveRequestPath,
} from './buildAxiosErrorMeta';

const internalApi = axios.create({
  baseURL: '/api/internal',
});

const api = axios.create({
  baseURL: '/api',
});

const socketInterceptor = req => {
  const methods = ['post', 'put', 'delete'];

  if (methods.includes(req.method)) {
    req.headers.socketId = app.socket?.id || '';
  }

  return req;
};

const requestIdInterceptor = req => {
  const requestId = uuidv4();

  req.headers['X-Request-Id'] = requestId;
  req.meta = req.meta || {};
  req.meta.requestId = requestId;

  return req;
};

const metaInterceptor = req => {
  req.meta = req.meta || {};
  req.meta.requestStartedAt = new Date().getTime();

  if (req.data) {
    req.meta.data = req.data;
  }

  return req;
};

api.interceptors.request.use(requestIdInterceptor);
internalApi.interceptors.request.use(requestIdInterceptor);
api.interceptors.request.use(metaInterceptor);
internalApi.interceptors.request.use(metaInterceptor);

api.interceptors.request.use(socketInterceptor);
internalApi.interceptors.request.use(socketInterceptor);

// Path-only (no method): a recent PUT 200 explains a following POST Network Error on the same path.
const RECENT_OK_MAX_KEYS = 50;
const recentEndpointOkAt = new Map();

function rememberEndpointOk(config) {
  const path = resolveRequestPath(config);

  if (!path) {
    return;
  }
  if (recentEndpointOkAt.size >= RECENT_OK_MAX_KEYS && !recentEndpointOkAt.has(path)) {
    const oldest = recentEndpointOkAt.keys().next().value;

    recentEndpointOkAt.delete(oldest);
  }
  recentEndpointOkAt.set(path, Date.now());
}

function recentSameEndpointOkMsAgo(config) {
  const path = resolveRequestPath(config);

  if (!path) {
    return undefined;
  }
  const okAt = recentEndpointOkAt.get(path);

  if (okAt == null) {
    return undefined;
  }

  return Date.now() - okAt;
}

function buildStoreApiClientContext(error) {
  const ctx = buildBrowserClientContext();

  if (app.socket && typeof app.socket.connected === 'boolean') {
    ctx.socketConnected = app.socket.connected;
  }

  const recentOkMsAgo = recentSameEndpointOkMsAgo(error.config);

  if (typeof recentOkMsAgo === 'number') {
    ctx.recentSameEndpointOkMsAgo = recentOkMsAgo;
  }

  return ctx;
}

function enrichAndThrowStoreApiError(error, clientKey) {
  const { meta, errorName, capturedMessage } = buildAxiosErrorMeta(
    error,
    clientKey,
    buildStoreApiClientContext(error),
  );

  error._meta = meta;
  error._errorName = errorName;
  error._capturedMessage = capturedMessage;
  throw error;
}

function trackOkResponse(response) {
  rememberEndpointOk(response.config);

  return response;
}

// ── API error toasts ─────────────────────────────────────────────────────────
// Three failure classes (Bugsnag _errorName buckets). Each sticky toast is gated
// so a burst never stacks duplicates of the same class:
//
//   1. NETWORK  — no HTTP response (STORE_API_ERROR_NETWORK_*). "Check internet".
//   2. GATEWAY  — HTML 502/503/504 from edge/maintenance (STORE_API_ERROR_GATEWAY).
//                 Same toast as network for now (retry / connection problem).
//   3. SERVER   — real API error body (STORE_API_ERROR_<status>_…). "Contact support".
//
// Toast is user-facing only. Independently every class is enriched and re-thrown
// → Bugsnag, grouped by _errorName (rate-limited per group, see config).

let activeNetworkToastId = null;
let activeServerToastId = null;

// Class 1: single sticky "connection problem" toast (internet drop fails many requests at once).
function toastNetworkConnection() {
  if (activeNetworkToastId !== null) {
    return;
  }

  activeNetworkToastId = toast.error(__('error_api_network_connection'), {
    timeout: false,
    onClose: () => {
      activeNetworkToastId = null;
    },
  });
}

// Class 2: single sticky "operation failed / contact support" toast — independent of class 1.
function toastStoreServerFailure() {
  if (activeServerToastId !== null) {
    return;
  }

  activeServerToastId = toast.error(__('error_tasks_api_operation'), {
    timeout: false,
    onClose: () => {
      activeServerToastId = null;
    },
  });
}

function toastInternalApiServerFailure(error) {
  const failUrl = error.config?.url;

  // File-size limit (4005) is a specific, actionable validation error — its own short
  // toast, kept out of the generic server gate so it isn't hidden by an open server toast.
  if (failUrl?.includes('/attachment') && error.response?.data?.code === 4005) {
    toast.error(__('fileUpload_limitStore', { limit: '' }));

    return;
  }

  if (failUrl?.includes('/ganttview')
    || failUrl?.includes('/resources')
    || failUrl?.includes('/custom_columns')
    || failUrl?.includes('/comments')
    || failUrl?.includes('/attachment')) {
    toastStoreServerFailure();
  }
}

function toastApiServerFailure(error) {
  const failUrl = error.config?.url;

  // Single OR-chain (not separate ifs): /tasks/custom_columns matches two fragments
  // (/task + /custom_columns) but must still toast only once.
  if (failUrl?.includes('/task')
    || failUrl?.includes('/link')
    || failUrl?.includes('/resources')
    || failUrl?.includes('/custom_columns')
    || failUrl?.includes('/history')) {
    toastStoreServerFailure();
  }
}

internalApi.interceptors.response.use(trackOkResponse,
  error => {
    if (isStoreApiNetworkFailure(error) || isStoreApiGatewayFailure(error)) {
      toastNetworkConnection();
    } else {
      toastInternalApiServerFailure(error);
    }

    enrichAndThrowStoreApiError(error, 'internalApi');
  });

api.interceptors.response.use(trackOkResponse,
  error => {
    if (isStoreApiNetworkFailure(error) || isStoreApiGatewayFailure(error)) {
      toastNetworkConnection();
    } else {
      toastApiServerFailure(error);
    }

    enrichAndThrowStoreApiError(error, 'api');
  });

// old webix support

const debouncedToastCall = _.debounce(locale => {
  toast.error(locale ? __(locale) : __('error_tasks_api_operation'));
}, 300);

webix.attachEvent('onBeforeAjax', (mode, url, data, request, headers, files, promise) => {
  headers.socketId = app.socket.id || '';
  promise.then(() => {}, () => {
    debouncedToastCall();
  });
});

export {
  internalApi,
  api,
};
